import { useState } from 'react';
import './SpecialProductsListing.css';
import { ApiProduct } from '../../../types';
import { Link } from 'react-router-dom';
import { QuantityInput } from '../../Form/QuantityInput';
import { useCart } from '../../../state/cart';
import { useTranslation } from '../../../i18n/i18n';
import { Img } from '../../Img/Img';
import { Button } from '../../Form/Button';
import { DeliveryTime } from '../DeliveryTime';
import { useConfig } from '../../../config';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { PriceTagSimple } from '../../../components/PriceTagSimple/PriceTagSimple';
import { useCriteo } from '../../../hooks/useCriteo';
import { ProductContainer } from '../../../state/product';
import { FlyToCartContainer } from '../../../components/FlyToCart/FlyToCart';
import { useLoop54Events } from '../../../hooks/useLoop54Events';
import { MetaContainer } from '../../../state/meta';

type SpecialProductsListingProps = {
	products: ApiProduct[];
	activeIndex?: number;
};
export const SpecialProductsListing = ({ products, activeIndex }: SpecialProductsListingProps): JSX.Element => {
	return (
		<div className="product-wrapper special-products">
			{products.map((product, index) => (
				<SpecialProductRow key={product.id} product={product} highlight={index === activeIndex} />
			))}
		</div>
	);
};

const SpecialProductRow = ({ product, highlight }: { product: ApiProduct; highlight: boolean }): JSX.Element => {
	const { setQuantity, currentCount } = useCart();
	const [quantityField, setQuantityField] = useState(product.bundleSize);
	const { flyToCart } = FlyToCartContainer.useContainer();
	const t = useTranslation().product;
	const { currencyCode } = useConfig();
	const { sendEvent, sendTorkEvent } = useAnalytics();
	const { sendCriteoEvent } = useCriteo();
	const { getFinalPrice } = ProductContainer.useContainer();
	const { store } = useConfig();
	const [hover, setHover] = useState(false);
	const { sendLoop54Event } = useLoop54Events();
	const { marketing } = MetaContainer.useContainer().trackingConsents;

	const handleAddToCart = (id: number) => {
		setQuantity(id, currentCount(id) + quantityField);
		flyToCart();
		sendEvent({
			event: 'add_to_cart',
			ecommerce: {
				value: product.price,
				currency: currencyCode,
				items: [
					{
						item_id: String(id),
						item_name: product.name,
						price: product.price,
						quantity: quantityField,
					},
				],
			},
		});

		// TODO: test if this fires for tork products (not sure if product.brandName exists here or if we need to check against product.name)
		if (store === 'kgno' && product.brandName?.toLowerCase() === 'tork') {
			sendTorkEvent('AddToCart', {
				revenue: product.price * quantityField,
			});
		}

		sendLoop54Event({ events: [{ type: 'addtocart', entity: { type: 'Product', id: product.id + '' } }] });

		sendCriteoEvent({
			event: 'addToCart',
			item: [
				{
					id: product.id + '',
					price: product.price,
					quantity: quantityField,
				},
			],
		});

		if (typeof window.fbq !== 'undefined') {
			if (store === 'kgno') {
				if (product.brandName?.toLowerCase() === 'tork' && marketing) {
					window.fbq('track', 'AddToCart', {
						value: product.price * quantityField,
						currency: 'NOK',
					});
				}
			} else {
				window.fbq('track', 'AddToCart', {
					content_type: 'product',
					content_ids: [id + ''],
					content_name: product.name,
					value: quantityField * price,
					currency: currencyCode,
				});
			}
		}
	};

	const { id, displayId, name, image, perma, bundleSize, price, campaignPrice, deliveryClass, vatRate } = product;
	const productNotInStock = deliveryClass === 3 || deliveryClass === 4;

	return (
		<div className="row" onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
			<div
				className={`product-image ${productNotInStock ? 'product-not-in-stock-color-light' : ''} ${
					hover || highlight ? 'highlight-row' : ''
				}`}
			>
				<Link to={'/' + perma}>
					<Img src={image} width={50} height={50} imgWidth={50} imgHeight={50} alt={name} />
				</Link>
			</div>
			<div
				className={`product-details-wrapper ${productNotInStock ? 'product-not-in-stock-color-light' : ''} ${
					hover || highlight ? 'highlight-row' : ''
				}`}
			>
				<div className="product-link">
					<Link to={'/' + perma}>{name}</Link>
				</div>
				<div className="delivery-article-number-wrapper">
					<div className="delivery-time">
						<DeliveryTime deliveryClass={deliveryClass} textLength={'short-one-line'} />
					</div>
					<div className="article-number not-mobile">
						{t.articleNumber} {displayId}
					</div>
				</div>
				<div className="price">
					<PriceTagSimple
						price={getFinalPrice(id, price, 1, campaignPrice)}
						vatRate={vatRate}
						isCampaign={campaignPrice > 0}
					></PriceTagSimple>
				</div>
				<div className="quantity-input-buy-button-wrapper">
					<div className="not-mobile">
						<QuantityInput
							defaultValue={quantityField}
							onBlur={(value) => setQuantityField(value)}
							step={bundleSize}
						/>
					</div>
					<div className="buy-button">
						<Button
							className={`buy-button ${productNotInStock ? 'product-not-in-stock-color' : ''}`}
							onClick={() => handleAddToCart(id)}
						>
							{t.buyNow}
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
};
